import { fn } from '@ember/helper';
import { action } from '@ember/object';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { restartableTask, timeout } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import { pluralize } from 'ember-inflector';
import { gt } from 'ember-truth-helpers';
import MultiFlowSelect from 'garaje/components/multi-flow-select';
import ConfirmationModal from 'garaje/components/ui/classic/confirmation-modal';
import UiHeader from 'garaje/components/ui/header';
import UiSearch from 'garaje/components/ui-search';
import arrayToSentence from 'garaje/helpers/array-to-sentence';
import type Company from 'garaje/models/company';
import type Flow from 'garaje/models/flow';
import type GlobalFlow from 'garaje/models/global-flow';
import type Location from 'garaje/models/location';
import type Questionnaire from 'garaje/models/questionnaire';
import type FlashMessagesService from 'garaje/services/flash-messages';
import zft from 'garaje/utils/zero-for-tests';
import { cached, localCopy } from 'tracked-toolbox';

import EmptyState from './list/empty-state';
import LoadMore from './list/load-more';
import QuestionnairesListLoader, { type Filters } from './list/loader';
import Table from './list/table';

const DEBOUNCE_TIME = 250; // debounce multiple characters so we don't bother filtering while the user is still typing

type UpdateableQueryParams = 'flow' | 'sort' | 'title';

interface VisitorsQuestionnairesListSignature {
  Args: {
    /**
     * Whether the current user can create new questionnaires.
     */
    canCreateQuestionnaires: boolean;
    /**
     * Whether the current user can delete questionnaires.
     */
    canDeleteQuestionnaires: boolean;
    /**
     * Whether the current user can update questionnaires.
     */
    canUpdateQuestionnaires: boolean;
    /**
     * If specified, list questionnaires for this company.
     * If both `@company` and `@location` are specified, `@company` is used.
     * One of `@company` or `@location` must be specified.
     */
    company?: Company;
    /**
     * The route to use as the target for the "Create" button.
     */
    createRoute: string;
    /**
     * If specified, filter questionnaires by flows (comma-separated list of flow IDs).
     */
    flowFilter?: string;
    flows: Array<Flow | GlobalFlow>;
    /**
     * If true, add `filter[global-only]` to request only global questionnaires.
     */
    globalOnly?: boolean;
    /**
     * If specified, list questionnaires for this location.
     * If both `@company` and `@location` are specified, `@company` is used.
     * One of `@company` or `@location` must be specified.
     */
    location?: Location;
    sort: string;
    /**
     * The route to use as the target for the "View" or "Edit" action.
     */
    showRoute: string;
    /**
     * If specified, filter questionnaires by title.
     */
    titleFilter?: string;
    /**
     * Callback to update a query param when filtering changes.
     */
    updateQueryParam: (name: UpdateableQueryParams, value: string | null) => void;
  };
}

export default class VisitorsQuestionnairesList extends Component<VisitorsQuestionnairesListSignature> {
  @service declare flashMessages: FlashMessagesService;

  @tracked _deleteConfirmationState: { questionnaire: Questionnaire; reload: () => void } | null = null;

  get deleteConfirmationTitleText(): string {
    if (!this._deleteConfirmationState) return '';
    return `Are you sure you want to delete ${this._deleteConfirmationState.questionnaire.title}?`;
  }

  @cached
  get filters(): Filters {
    const filters: Filters = {};
    if (this.args.titleFilter) {
      filters['title'] = this.args.titleFilter;
    }
    if (this.args.flowFilter) {
      filters['flow'] = this.args.flowFilter;
    }
    if (this.args.globalOnly) {
      filters['global-only'] = true;
    }
    return filters;
  }

  get isFiltered(): boolean {
    return Object.values(this.filters).some((value) => value !== null && value !== '');
  }

  get isShowingDeleteConfirmation(): boolean {
    return !!this._deleteConfirmationState;
  }

  get selectedFlows(): Array<Flow | GlobalFlow> {
    if (!this.args.flowFilter) return [];
    const selectedFlowIds = new Set(this.args.flowFilter.split(','));
    return this.args.flows.filter((flow) => selectedFlowIds.has(flow.id));
  }

  /**
   * Maintain local state for the "title" value; this represents what the user has
   * typed in the box, not necessarily what's being used for filtering (on account
   * of the debounce). We use this property as the `@value` arg for the search field
   * so the user's input shows up immediately, but rely on `@titleFilter` to filter.
   */
  @localCopy('args.titleFilter') declare title: string | undefined;

  @action
  confirmQuestionnaireDeletion(reload: () => void, questionnaire: Questionnaire): void {
    this._deleteConfirmationState = {
      questionnaire,
      reload,
    };
  }

  @action
  async deleteQuestionnaire(): Promise<void> {
    if (!this._deleteConfirmationState) return;
    await this._deleteConfirmationState.questionnaire.destroyRecord();
    this.flashMessages.showAndHideFlash('success', 'Assessment deleted');
    this._deleteConfirmationState.reload();
    this._deleteConfirmationState = null;
  }

  @action
  hideDeleteConfirmation(): void {
    this._deleteConfirmationState = null;
  }

  isFilteredBy(filterName: keyof Filters): boolean {
    return (
      Object.hasOwn(this.filters, filterName) && this.filters[filterName] !== null && this.filters[filterName] !== ''
    );
  }

  @action
  titleText(questionnaireCount: number): string {
    let text = pluralize(questionnaireCount, 'Assessment', { withoutCount: questionnaireCount === 0 });
    if (questionnaireCount > 0 && this.isFiltered) {
      const matchPieces: string[] = [];
      if (this.isFilteredBy('title')) {
        matchPieces.push(`"${this.filters.title}"`);
      }
      if (this.isFilteredBy('flow')) {
        const selectedFlowCount = (this.filters.flow ?? '').split(',').length;
        matchPieces.push(
          pluralize(selectedFlowCount, 'selected visitor type', {
            withoutCount: selectedFlowCount === 1,
          }),
        );
      }
      if (matchPieces.length > 0) {
        text = `${text} matching ${arrayToSentence(matchPieces)}`;
      }
    }
    return text;
  }

  @action
  updateFlowFilter(selectedFlows: Array<Flow | GlobalFlow>): void {
    this.args.updateQueryParam('flow', selectedFlows.map((flow) => flow.id).join(','));
  }

  @action
  updateSort(sortBy: string, sortDirection: 'asc' | 'desc'): void {
    let sort = sortBy;
    if (sortDirection === 'desc') sort = `-${sort}`;
    this.args.updateQueryParam('sort', sort);
  }

  updateTitleFilter = restartableTask(async (event: InputEvent) => {
    const title = (event.target as HTMLInputElement).value;
    this.title = title;
    await timeout(zft(DEBOUNCE_TIME));

    this.args.updateQueryParam('title', title);
  });

  <template>
    <QuestionnairesListLoader @company={{@company}} @location={{@location}} @filters={{this.filters}} @sort={{@sort}}>
      <:header as |count|>
        <div class="flex items-center">
          <div class="flex-auto min-w-0 min-h-0">
            <UiHeader data-test-questionnaires-header>
              <:title>
                {{this.titleText count}}
              </:title>
            </UiHeader>
          </div>
          <div class="flex-none flex">
            <UiSearch
              @onInput={{perform this.updateTitleFilter}}
              @placeholder="Search assessments"
              @value={{this.title}}
              class="mr-4 !w-64"
              data-test-questionnaire-title-filter
            />
            {{#if @canCreateQuestionnaires}}
              <LinkTo @route={{@createRoute}} class="btn btn--brand" data-test-create-questionnaire-button>
                New assessment
              </LinkTo>
            {{/if}}
          </div>
        </div>

        {{#if (gt @flows.length 1)}}
          <div data-test-questionnaire-flow-filter>
            <MultiFlowSelect
              @flows={{@flows}}
              @renderInPlace={{false}}
              @selectedFlows={{this.selectedFlows}}
              @selectedFlowsChanged={{this.updateFlowFilter}}
              @triggerClass="w-64 truncate"
            />
          </div>
        {{/if}}
      </:header>

      <:list as |questionnaires loader reload|>
        <Table
          @canDeleteQuestionnaires={{@canDeleteQuestionnaires}}
          @canUpdateQuestionnaires={{@canUpdateQuestionnaires}}
          @changeSort={{this.updateSort}}
          @deleteQuestionnaire={{fn this.confirmQuestionnaireDeletion reload}}
          @questionnaires={{questionnaires}}
          @showRoute={{@showRoute}}
          @sort={{@sort}}
          class="questionnaires-table"
        >
          <:footer as |columns|>
            <tr>
              <td colspan={{columns}}>
                <LoadMore @loader={{loader}} />
              </td>
            </tr>
          </:footer>
        </Table>
      </:list>

      <:empty>
        <EmptyState
          @canCreateQuestionnaires={{@canCreateQuestionnaires}}
          @createRoute={{@createRoute}}
          @isFiltered={{this.isFiltered}}
        />
      </:empty>
    </QuestionnairesListLoader>

    {{#if this.isShowingDeleteConfirmation}}
      <ConfirmationModal
        @closeButtonClass="btn--outline"
        @ctaText="Delete"
        @handleTaskAction={{this.deleteQuestionnaire}}
        @onClose={{this.hideDeleteConfirmation}}
        @showTopRightCloseButton={{true}}
        @title={{this.deleteConfirmationTitleText}}
      >
        <p class="pt-8 !mb-0">You will not be able to recover it.</p>
      </ConfirmationModal>
    {{/if}}
  </template>
}
